import { useMemo } from 'react';

import { useLocation } from 'react-router';

import { useNavigationContext } from '../../context/NavigationContext';
import resolveFullPath from '../../utils/resolveFullPath';
import { getShouldBeOpen } from './openPaths';

import type { NavigationItemData } from './index';

export const useNavigationItemState = (
  item: NavigationItemData,
  parentPath: string,
  level: number
) => {
  const location = useLocation();
  const { openPaths, isMenuOpen } = useNavigationContext();

  const fullPath = useMemo(() => resolveFullPath(parentPath, item.path), [parentPath, item.path]);
  const hasChildren = !!item.children;

  const isAnyDescendantActive = useMemo(() => {
    const check = (node: NavigationItemData, basePath: string): boolean => {
      const nodePath = resolveFullPath(basePath, node.path);
      const hasOwnPath = Boolean(node.path);
      if (
        hasOwnPath &&
        (location.pathname === nodePath || location.pathname.startsWith(`${nodePath}/`))
      )
        return true;
      return node.children?.some(child => check(child, nodePath)) ?? false;
    };
    return check(item, parentPath);
  }, [item, parentPath, location.pathname]);

  const shouldBeOpen = useMemo(
    () =>
      getShouldBeOpen({
        openPaths,
        level,
        isAnyDescendantActive,
        isMenuOpen,
        fullPath,
        hasItemPath: Boolean(item.path)
      }),
    [openPaths, level, item.path, isAnyDescendantActive, fullPath, isMenuOpen]
  );

  const isSelected = useMemo(() => {
    if (!hasChildren) {
      return location.pathname === fullPath || location.pathname.startsWith(`${fullPath}/`);
    }
    if (!isMenuOpen) {
      return location.pathname === fullPath || isAnyDescendantActive;
    }
    return location.pathname === fullPath || (isAnyDescendantActive && !shouldBeOpen);
  }, [hasChildren, isMenuOpen, location.pathname, fullPath, isAnyDescendantActive, shouldBeOpen]);

  return { fullPath, hasChildren, isAnyDescendantActive, shouldBeOpen, isSelected };
};
